import { useEffect, useState, useCallback } from 'react'
import { ShieldAlert, RefreshCw, Trash2, ChevronDown, Eye, Clock } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { adminAPI } from '@/lib/api'

/* Kaart voor mislukte logins.

   Bovenaan: verdachte IPs — geaggregeerd, laatste 24u, drie pogingen of meer.
   Zo zie je in één blik wie bezig is. Onderaan: klik om de ruwe log te zien
   (laatste 100 pogingen) — soms wil je gewoon zien welke usernames worden
   geprobeerd. */

const fmtRel = (iso) => {
  if (!iso) return '—'
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60) return `${sec}s geleden`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m geleden`
  const uur = Math.floor(min / 60)
  if (uur < 24) return `${uur}u geleden`
  return `${Math.floor(uur / 24)}d geleden`
}

export default function FailedLoginsCard() {
  const [ips, setIps]     = useState([])
  const [log, setLog]     = useState([])
  const [openLog, setOpen] = useState(false)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout]   = useState(null)

  const laadIps = useCallback(() => {
    adminAPI.getFailedLoginIps()
      .then(r => setIps(r.data?.ips ?? []))
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }, [])
  const laadLog = useCallback(() => {
    adminAPI.getFailedLoginLog()
      .then(r => setLog(r.data?.entries ?? []))
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }, [])

  useEffect(() => { laadIps() }, [laadIps])
  useEffect(() => { if (openLog) laadLog() }, [openLog, laadLog])

  const opruimen = async () => {
    if (!window.confirm('Records ouder dan 30 dagen wissen?')) return
    setBezig(true)
    try { await adminAPI.cleanupFailedLogins(); laadIps(); if (openLog) laadLog() }
    catch (e) { setFout(e.response?.data?.error ?? e.message) }
    finally { setBezig(false) }
  }

  const kleurVoorAantal = (n) => {
    if (n >= 20) return '#FF5A5A'
    if (n >= 10) return '#FFB57A'
    return '#94A3B8'
  }

  return (
    <Card className="mb-4" style={{ borderLeft: '2px solid #FF5A5A' }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-signal" /> Mislukte logins
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Verdachte IPs van de laatste 24 uur. De rate-limit gooit ze al na 10 pogingen
          uit; deze lijst maakt zichtbaar wanneer het gebeurt.
        </p>
      </CardHeader>
      <CardContent>
        {fout && <p className="text-sm text-signal mb-2">{fout}</p>}

        {ips.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Geen verdachte activiteit in de laatste 24 uur.
          </p>
        ) : (
          <div className="space-y-1.5">
            {ips.map(ip => (
              <div key={ip.ip}
                   className="flex items-center gap-3 rounded-[4px] border border-border bg-card px-3 py-2 flex-wrap"
                   style={{ borderLeft: `2px solid ${kleurVoorAantal(Number(ip.pogingen))}` }}>
                <code className="text-sm font-mono w-32 shrink-0">{ip.ip}</code>
                <span className="font-bold tabular-nums text-sm"
                      style={{ color: kleurVoorAantal(Number(ip.pogingen)) }}>
                  {ip.pogingen}
                </span>
                <span className="text-xs text-muted-foreground">pogingen</span>
                {Number(ip.verschillende_users) > 1 && (
                  <span className="text-xs text-peach">
                    · {ip.verschillende_users} verschillende namen
                  </span>
                )}
                <span className="ml-auto text-xs text-muted-foreground shrink-0">
                  <Clock className="h-3 w-3 inline mr-1" />
                  {fmtRel(ip.laatst)}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mt-3 flex-wrap">
          <Button size="sm" variant="ghost" onClick={laadIps} disabled={bezig}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Verversen
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(o => !o)}>
            <Eye className="h-4 w-4 mr-1.5" />
            {openLog ? 'Log sluiten' : 'Ruwe log tonen'}
            <ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${openLog ? 'rotate-180' : ''}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={opruimen} disabled={bezig}
                  className="text-muted-foreground ml-auto">
            <Trash2 className="h-4 w-4 mr-1.5" /> Records &gt;30 dagen wissen
          </Button>
        </div>

        {openLog && (
          <div className="mt-3 pt-3 border-t border-border/40">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Laatste 100 pogingen
            </div>
            {log.length === 0 ? (
              <p className="text-sm text-muted-foreground">Log is leeg.</p>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="font-mono uppercase tracking-widest text-muted-foreground text-left">
                      <th className="py-1 pr-2">Tijd</th>
                      <th className="py-1 pr-2">Gebruikersnaam</th>
                      <th className="py-1 pr-2">IP</th>
                      <th className="py-1">Reden</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.map(e => (
                      <tr key={e.id} className="border-b border-border/30 last:border-0">
                        <td className="py-1 pr-2 text-muted-foreground whitespace-nowrap">
                          {fmtRel(e.created_at)}
                        </td>
                        <td className="py-1 pr-2 font-mono">{e.username_attempted || '—'}</td>
                        <td className="py-1 pr-2 font-mono">{e.ip || '—'}</td>
                        <td className="py-1 text-muted-foreground">
                          {e.reason === 'no-user' ? 'onbestaande naam'
                          : e.reason === 'wrong-password' ? 'fout wachtwoord'
                          : e.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
